import React, { useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useBenefitClaimType, usePolicyInfo } from 'all-in-one-shared-api';

import BenefitContext from 'pages/benefits/contexts/BenefitContext';

import Menu, { MenuProp, MenuItem } from 'components/menu/Menu';
import { MENU_ITEMS } from 'pages/benefits/constants/MenuItems';

import { SWR_KEY } from 'pages/benefits/types/benefitTypes';
import { getCurrentPolicy } from 'pages/benefits/utils/utils';

import { useKeycloakReady } from 'hooks/useKeycloakReady';

import { getFamilyRelationshipByCode } from 'utils/benefits';

type BenefitMenuProp = Omit<MenuProp, 'items' | 'menuStyles' | 'menuItemStyles'>;

function BenefitMenu(props: BenefitMenuProp) {
  const { t } = useTranslation();
  const { ready, token } = useKeycloakReady();

  const { shouldFetch: policyShouldFetch, data: policyInfoData } = usePolicyInfo(SWR_KEY.POLICY_INFO, token || '');

  const { selectedFilterName, selectedFilterPeriod } = useContext(BenefitContext);
  const currentPolicy = getCurrentPolicy(policyInfoData, selectedFilterName, selectedFilterPeriod);

  const renewalNumber = currentPolicy?.renewalNumber ?? -1;

  const familyRelationship = getFamilyRelationshipByCode(
    currentPolicy?.familyRelationship ?? '',
    currentPolicy?.memberType ?? '',
  );
  const { memberType, familyRelation } = familyRelationship ?? {};

  const { shouldFetch: claimTypeShouldFetch, data: claimTypeData } = useBenefitClaimType(
    { name: SWR_KEY.BENEFIT_CLAIM_TYPE, memberType, familyRelation, renewalNumber },
    token ?? '',
    memberType ?? -1,
    familyRelation ?? '',
    renewalNumber,
  );

  const updatedItems: MenuItem[] = MENU_ITEMS.filter((item) => claimTypeData?.includes(item.id)).map((item) => {
    return { ...item, label: t(item.label) };
  });

  useEffect(() => {
    policyShouldFetch(ready);
  }, [policyShouldFetch, ready]);

  useEffect(() => {
    claimTypeShouldFetch(!!policyInfoData && memberType != undefined && !!familyRelation && renewalNumber !== -1);
  }, [claimTypeShouldFetch, policyInfoData, memberType, familyRelation, renewalNumber]);

  return (
    <Menu
      {...props}
      items={updatedItems}
      menuStyles={{ marginTop: '16px', justifyContent: 'space-between', padding: '0 8px' }}
      menuItemStyles={{ padding: '0 12px' }}
    />
  );
}

export default BenefitMenu;
